import { ImageGenerationSettings, GeneratedResult, ImageModelId, AIGeneratorApiKeys } from '../../../core/types/image-generation.types';
import { resolveApiKey } from '../../utils/api-keys';
import { shouldUseProxy, proxyGenerateContent } from '../ai-proxy';

// Maps AI Generator model IDs to existing image provider backends
const MODEL_PROVIDER_MAP: Record<ImageModelId, { provider: 'pollinations' | 'huggingface' | 'dalle' | 'gemini'; model?: string }> = {
  'soul-2.0': { provider: 'gemini', model: 'gemini-2.5-flash-image' },
  'nano-banana-pro': { provider: 'pollinations' },
  'nano-banana-2': { provider: 'gemini', model: 'gemini-3.1-flash-image-preview' },
  'seedream-5.0': { provider: 'huggingface', model: 'stabilityai/stable-diffusion-xl-base-1.0' },
  'flux-kontext': { provider: 'huggingface', model: 'black-forest-labs/FLUX.1-schnell' },
  'gpt-image-1.5': { provider: 'dalle' },
};

interface GenerateOptions {
  settings: ImageGenerationSettings;
  apiKeys: AIGeneratorApiKeys;
  onProgress?: (percentage: number) => void;
  onResult?: (result: GeneratedResult, index: number) => void;
  signal?: AbortSignal;
}

const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Get the API key for a provider: user-entered key takes priority, then env var.
 */
function getApiKey(provider: string, apiKeys: AIGeneratorApiKeys): string {
  const storeKey = (apiKeys as Record<string, string>)[provider] ?? '';
  return resolveApiKey(provider, storeKey);
}

/**
 * Build the effective prompt by combining user prompt with style, quality, dimensions, and reference context.
 */
function buildEffectivePrompt(settings: ImageGenerationSettings): string {
  // Slot generator provides a pre-built structured prompt — use it directly
  if (settings.effectivePrompt) {
    return settings.effectivePrompt;
  }

  let prompt = settings.prompt;

  if (settings.style) {
    prompt = `${prompt}, ${settings.style} style`;
  }

  if (settings.qualityPreset === '4K') {
    prompt = `${prompt}, ultra high quality, highly detailed, 4K resolution`;
  } else if (settings.qualityPreset === '2K') {
    prompt = `${prompt}, high quality, detailed, 2K resolution`;
  } else if (settings.qualityPreset === '1K') {
    prompt = `${prompt}, high quality`;
  }

  prompt = `${prompt}, ${settings.width}x${settings.height}`;

  if (settings.negativePrompt) {
    prompt = `${prompt}. Avoid: ${settings.negativePrompt}`;
  }

  if (settings.removeBackground) {
    prompt = `${prompt}, transparent background, no background, isolated subject, PNG with alpha channel`;
  }

  if (settings.referenceImages.length > 0) {
    const refContext = settings.referenceImages.map(r => r.fileName).join(', ');
    prompt = `${prompt}. Reference style from: ${refContext}`;
  }

  return prompt;
}

/**
 * Generate image using Pollinations free API.
 */
async function generateWithPollinations(
  prompt: string,
  settings: ImageGenerationSettings,
  signal?: AbortSignal
): Promise<string> {
  const encodedPrompt = encodeURIComponent(prompt);
  let url = `https://image.pollinations.ai/prompt/${encodedPrompt}?width=${settings.width}&height=${settings.height}&nologo=true`;

  if (settings.qualityPreset === '2K' || settings.qualityPreset === '4K') {
    url += '&enhance=true';
  }

  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Pollinations error: ${response.status}`);

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Resize an image blob to exact target dimensions using an offscreen canvas.
 */
async function resizeImageBlob(blob: Blob, targetWidth: number, targetHeight: number): Promise<Blob> {
  const blobUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image for resize'));
      image.src = blobUrl;
    });

    if (Math.abs(img.naturalWidth - targetWidth) <= 1 && Math.abs(img.naturalHeight - targetHeight) <= 1) {
      return blob;
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas 2d context');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => result ? resolve(result) : reject(new Error('Canvas toBlob failed')),
        blob.type || 'image/png',
        0.95
      );
    });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Convert a Blob to a base64 string (without the data URL prefix).
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Generate image using Google Gemini.
 */
async function generateWithGemini(
  prompt: string,
  settings: ImageGenerationSettings,
  modelId: string,
  apiKeys: AIGeneratorApiKeys,
  signal?: AbortSignal
): Promise<string> {
  const aspectRatio = settings.aspectRatio === 'free' ? '1:1' : settings.aspectRatio;

  // Build parts array (reference images + prompt) — shared by both paths
  const parts: Array<{ text: string } | { inlineData: { mimeType: string; data: string } }> = [];

  for (const ref of settings.referenceImages) {
    try {
      const blob = await fetch(ref.url).then(r => r.blob());
      const base64 = await blobToBase64(blob);
      parts.push({
        inlineData: {
          mimeType: blob.type || 'image/png',
          data: base64,
        },
      });
    } catch {
      // Skip failed reference images silently
    }
  }

  parts.push({ text: prompt });

  const generationConfig = {
    responseModalities: ['TEXT', 'IMAGE'],
    imageConfig: { aspectRatio },
  };

  const systemInstruction = settings.systemInstruction
    ? { parts: [{ text: settings.systemInstruction }] }
    : undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any;

  if (shouldUseProxy()) {
    // Secure path: route through backend proxy (API key never reaches browser)
    data = await proxyGenerateContent(
      {
        model: modelId,
        contents: [{ parts }],
        generationConfig,
        systemInstruction,
      },
      signal,
    );
  } else {
    // Fallback: direct call with user's own API key
    const apiKey = getApiKey('google', apiKeys);
    if (!apiKey) {
      throw new Error('Google API key required for this model. Configure in Settings (gear icon).');
    }

    const requestBody: Record<string, unknown> = {
      contents: [{ parts }],
      generationConfig,
    };
    if (systemInstruction) {
      requestBody.systemInstruction = systemInstruction;
    }

    const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:generateContent`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': apiKey,
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Google Gemini error (${response.status}): ${errorText}`);
    }

    data = await response.json();
  }

  // Response parsing — identical for both paths
  const candidate = data.candidates?.[0];
  if (!candidate) throw new Error('No candidate in Gemini response');

  const imagePart = candidate.content?.parts?.find(
    (p: { inlineData?: { mimeType: string } }) => p.inlineData?.mimeType?.startsWith('image/')
  );

  if (!imagePart?.inlineData) {
    throw new Error('No image data in Gemini response');
  }

  const byteString = atob(imagePart.inlineData.data);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }
  const rawBlob = new Blob([bytes], { type: imagePart.inlineData.mimeType });
  const resizedBlob = await resizeImageBlob(rawBlob, settings.width, settings.height);
  return URL.createObjectURL(resizedBlob);
}

/**
 * Generate image using HuggingFace Inference API.
 */
async function generateWithHuggingFace(
  prompt: string,
  settings: ImageGenerationSettings,
  modelId: string,
  apiKeys: AIGeneratorApiKeys,
  signal?: AbortSignal
): Promise<string> {
  const apiKey = getApiKey('huggingface', apiKeys);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  const parameters: Record<string, unknown> = {
    width: settings.width,
    height: settings.height,
  };

  if (settings.negativePrompt) {
    parameters.negative_prompt = settings.negativePrompt;
  }

  const response = await fetch(`https://api-inference.huggingface.co/models/${modelId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ inputs: prompt, parameters }),
    signal,
  });

  if (!response.ok) throw new Error(`HuggingFace error: ${response.status}`);

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Generate image using DALL-E (OpenAI) API.
 */
async function generateWithDalle(
  prompt: string,
  settings: ImageGenerationSettings,
  apiKeys: AIGeneratorApiKeys,
  signal?: AbortSignal
): Promise<string> {
  const openaiKey = getApiKey('openai', apiKeys);

  if (!openaiKey) {
    throw new Error('OpenAI API key required for GPT Image model. Configure in Settings (gear icon).');
  }

  let size = '1024x1024';
  if (settings.width > settings.height * 1.3) size = '1792x1024';
  else if (settings.height > settings.width * 1.3) size = '1024x1792';

  const quality = (settings.qualityPreset === '2K' || settings.qualityPreset === '4K') ? 'hd' : 'standard';

  let style: 'vivid' | 'natural' = 'vivid';
  if (settings.style === 'realistic' || settings.style === 'cinematic') {
    style = 'natural';
  }

  const response = await fetch('https://api.openai.com/v1/images/generations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${openaiKey}`,
    },
    body: JSON.stringify({
      model: 'dall-e-3',
      prompt,
      n: 1,
      size,
      quality,
      style,
    }),
    signal,
  });

  if (!response.ok) throw new Error(`DALL-E error: ${response.status}`);

  const data = await response.json();
  const imageUrl = data.data?.[0]?.url;

  if (!imageUrl) throw new Error('No image URL in DALL-E response');

  const imgResponse = await fetch(imageUrl, { signal });
  const blob = await imgResponse.blob();
  return URL.createObjectURL(blob);
}

/**
 * Generate a single image using the appropriate provider.
 */
async function generateSingleImage(
  settings: ImageGenerationSettings,
  apiKeys: AIGeneratorApiKeys,
  signal?: AbortSignal
): Promise<GeneratedResult> {
  const mapping = MODEL_PROVIDER_MAP[settings.model];
  if (!mapping) throw new Error(`Unknown model: ${settings.model}`);

  const effectivePrompt = buildEffectivePrompt(settings);
  // For non-Gemini providers, combine system instruction into prompt (they don't support systemInstruction)
  const combinedPrompt = settings.systemInstruction
    ? `${settings.systemInstruction}\n\n${effectivePrompt}`
    : effectivePrompt;
  let url: string;

  switch (mapping.provider) {
    case 'pollinations':
      url = await generateWithPollinations(combinedPrompt, settings, signal);
      break;
    case 'gemini':
      url = await generateWithGemini(effectivePrompt, settings, mapping.model || 'gemini-2.5-flash-image', apiKeys, signal);
      break;
    case 'huggingface':
      url = await generateWithHuggingFace(combinedPrompt, settings, mapping.model || '', apiKeys, signal);
      break;
    case 'dalle':
      url = await generateWithDalle(combinedPrompt, settings, apiKeys, signal);
      break;
    default:
      throw new Error(`Unknown provider: ${(mapping as { provider: string }).provider}`);
  }

  return {
    id: generateId(),
    type: 'image',
    url,
    prompt: settings.prompt,
    model: settings.model,
    width: settings.width,
    height: settings.height,
    style: settings.style,
    timestamp: Date.now(),
    status: 'completed' as const,
    metadata: {
      provider: mapping.provider,
      effectivePrompt: combinedPrompt,
      seedMode: settings.seedMode,
      seedValue: settings.seedValue,
      styleStrength: settings.styleStrength,
      referenceCount: settings.referenceImages.length,
    },
  };
}

/**
 * Delay helper.
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(signal.reason ?? new DOMException('Aborted', 'AbortError')); return; }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(timer); reject(signal.reason ?? new DOMException('Aborted', 'AbortError')); }, { once: true });
  });
}

/**
 * Main entry: generate images with concurrency-limited batch, progress, and cancellation.
 */
export async function generateImages(options: GenerateOptions): Promise<GeneratedResult[]> {
  const { settings, apiKeys, onProgress, onResult, signal } = options;
  const total = settings.batchCount;
  const CONCURRENCY = 2;
  const STAGGER_MS = 1500;

  onProgress?.(0);

  const results: GeneratedResult[] = [];
  let completedCount = 0;
  let failedCount = 0;

  const running = new Set<Promise<void>>();

  for (let i = 0; i < total; i++) {
    if (signal?.aborted) {
      throw signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    while (running.size >= CONCURRENCY) {
      await Promise.race(running);
    }

    if (i > 0) {
      await delay(STAGGER_MS, signal);
    }

    const index = i;
    const task = (async () => {
      try {
        const result = await generateSingleImage(settings, apiKeys, signal);
        results.push(result);
        completedCount++;
        onProgress?.(Math.round(((completedCount + failedCount) / total) * 100));
        onResult?.(result, index);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          throw err;
        }
        failedCount++;
        const failedResult: GeneratedResult = {
          id: generateId(),
          type: 'image',
          url: '',
          prompt: settings.prompt,
          model: settings.model,
          width: settings.width,
          height: settings.height,
          style: settings.style,
          timestamp: Date.now(),
          status: 'failed',
          error: err instanceof Error ? err.message : 'Generation failed',
          metadata: {},
        };
        results.push(failedResult);
        onProgress?.(Math.round(((completedCount + failedCount) / total) * 100));
        onResult?.(failedResult, index);
      }
    })();

    const tracked = task.then(
      () => { running.delete(tracked); },
      (err) => { running.delete(tracked); throw err; }
    );
    running.add(tracked);
  }

  try {
    await Promise.all(running);
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      await Promise.allSettled(running);
      throw err;
    }
    throw err;
  }

  if (completedCount === 0 && failedCount > 0) {
    throw new Error(`All ${failedCount} image(s) failed to generate`);
  }

  onProgress?.(100);
  return results;
}

/**
 * Get the provider backend for a given model ID (useful for testing).
 */
export function getModelProvider(modelId: ImageModelId) {
  return MODEL_PROVIDER_MAP[modelId];
}
